import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, Button, message, Space, Spin } from 'antd';
import html2canvas from 'html2canvas';

import bugReportsService from '../../services/bugReports.service';
import packageJson from '../../../package.json';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Wrapper class on the antd Modal so html2canvas can skip it — without this
// the modal itself ends up in the screenshot since the Modal portal mounts
// to document.body the moment open=true (before useEffect can capture).
const MODAL_WRAP_CLASS = 'bug-report-modal-wrap';

// Selectors for chrome that should never appear in a "page state" screenshot:
// the modal itself, the floating bug button, and the assorted antd overlay
// portals (tooltip, toast, notification, popover, dropdown menu).
const SKIP_FOR_CAPTURE_SELECTORS = [
  `.${MODAL_WRAP_CLASS}`,
  '.bug-reporter-button-wrap',
  '.ant-tooltip',
  '.ant-message',
  '.ant-notification',
  '.ant-popover',
  '.ant-dropdown',
];

const captureScreenshot = (): Promise<Blob | null> =>
  new Promise(resolve => {
    html2canvas(document.body, {
      useCORS: true,
      logging: false,
      scale: window.devicePixelRatio < 2 ? 1 : 1.5,
      ignoreElements: el => SKIP_FOR_CAPTURE_SELECTORS.some(sel => (el as Element).matches?.(sel)),
    })
      .then(canvas => canvas.toBlob(blob => resolve(blob), 'image/png'))
      .catch(err => {
        console.warn('Bug report screenshot capture failed:', err);
        resolve(null);
      });
  });

/**
 * User-facing bug report modal. Captures the page DOM via html2canvas the
 * moment it opens, with the modal/button/antd-overlays excluded via
 * ignoreElements so the screenshot reflects what the user was looking at
 * when they clicked the report button. Users can re-take, drop the
 * screenshot, or upload a custom image instead.
 */
const BugReportModal: React.FC<Props> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Capture the page on open. The modal IS already in the DOM by the time
  // this effect runs (Modal portals on open=true), so we rely on
  // ignoreElements in captureScreenshot() to exclude it.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setCapturing(true);

    // Defer briefly so any open menus/dropdowns the user had up before
    // clicking the bug button have time to settle. Under 250ms so users
    // don't wait noticeably.
    const t = setTimeout(() => {
      captureScreenshot().then(blob => {
        if (cancelled) return;
        if (blob) {
          setScreenshotBlob(blob);
          setScreenshotUrl(URL.createObjectURL(blob));
        }
        setCapturing(false);
      });
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open]);

  // Clean up blob URL when the modal closes or the screenshot changes.
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [screenshotUrl]);

  // Auto-fill a sensible default title from the URL path on open.
  useEffect(() => {
    if (open) {
      const path = window.location.pathname.replace(/^\//, '') || 'home';
      form.setFieldsValue({ title: path });
    }
  }, [open, form]);

  const handleClose = () => {
    if (submitting) return;
    setScreenshotBlob(null);
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setScreenshotUrl(null);
    form.resetFields();
    onClose();
  };

  const handleRetake = () => {
    setCapturing(true);
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setScreenshotUrl(null);
    setScreenshotBlob(null);
    captureScreenshot().then(blob => {
      if (blob) {
        setScreenshotBlob(blob);
        setScreenshotUrl(URL.createObjectURL(blob));
      }
      setCapturing(false);
    });
  };

  const handleDropScreenshot = () => {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setScreenshotUrl(null);
    setScreenshotBlob(null);
  };

  const handleUploadInstead = (file: File | undefined) => {
    if (!file) return;
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setScreenshotBlob(file);
    setScreenshotUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);

    // Pull the App Insights operation id if available — admins use it to
    // pivot from the report straight to the related telemetry trace.
    const aiOpId =
      // @ts-ignore — AppInsights global on window when loaded
      (window as any)?.appInsights?.context?.telemetryTrace?.traceID || undefined;

    try {
      const res = await bugReportsService.submit({
        title: values.title,
        description: values.description,
        url_at_submission: window.location.href,
        user_agent: navigator.userAgent,
        app_version: (packageJson as any).version,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        app_insights_operation_id: aiOpId,
        screenshot: screenshotBlob,
      });
      if (screenshotBlob && res.data?.screenshot_upload_failed) {
        message.warning(
          'Bug report saved, but the screenshot did not upload. An admin may follow up.'
        );
      } else {
        message.success('Bug report submitted — thanks for the heads-up.');
      }
      handleClose();
    } catch (err) {
      console.error('Bug report submit failed:', err);
      message.error('Failed to submit bug report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Report a bug"
      width={700}
      wrapClassName={MODAL_WRAP_CLASS}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={capturing}
        >
          Submit
        </Button>,
      ]}
      maskClosable={!submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ max: 255, message: 'Title is too long' }]}>
          <Input placeholder="Short summary (auto-filled from page)" />
        </Form.Item>

        <Form.Item
          name="description"
          label="What went wrong?"
          rules={[
            { required: true, message: 'A description is required' },
            { max: 5000, message: 'Description is too long' },
          ]}
        >
          <Input.TextArea
            rows={5}
            placeholder="What were you trying to do? What did you expect? What happened instead?"
          />
        </Form.Item>

        <Form.Item label="Screenshot">
          <Spin spinning={capturing} tip="Capturing page...">
            {screenshotUrl ? (
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 4 }}>
                <img
                  src={screenshotUrl}
                  alt="Page screenshot"
                  style={{ maxWidth: '100%', maxHeight: 220, display: 'block' }}
                />
              </div>
            ) : (
              <div
                style={{
                  border: '1px dashed #d9d9d9',
                  borderRadius: 4,
                  padding: 24,
                  textAlign: 'center',
                  color: '#999',
                }}
              >
                {capturing ? 'Capturing…' : 'No screenshot attached'}
              </div>
            )}
          </Spin>

          <Space style={{ marginTop: 8 }}>
            <Button size="small" onClick={handleRetake} disabled={capturing || submitting}>
              Re-take
            </Button>
            <Button
              size="small"
              onClick={handleDropScreenshot}
              disabled={!screenshotBlob || submitting}
            >
              Drop screenshot
            </Button>
            <Button
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              Upload instead
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: 'none' }}
              onChange={e => handleUploadInstead(e.target.files?.[0])}
            />
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BugReportModal;
